<template>
  <section class="voice-input">
    <header><div><b>语音输入</b><small>{{ statusText }}</small></div><van-icon name="volume-o" /></header>

    <div class="record-row">
      <button class="record-button" :class="{recording}" :disabled="transcribing || optimizing"
              @mousedown.prevent="pressStart" @mouseup.prevent="pressEnd" @mouseleave="pressEnd"
              @touchstart.prevent="touchStart" @touchend.prevent="touchEnd" @touchcancel.prevent="cancelPress">
        <van-icon :name="recording ? 'pause-circle-o' : 'volume-o'" />
        <span>{{ recording ? `录音中 ${duration}s` : '按住说话' }}</span>
      </button>
      <audio v-if="audioUrl" :src="audioUrl" controls preload="metadata" />
    </div>

    <div v-if="transcribing" class="processing"><van-loading size="18px" />正在转换文字</div>
    <template v-if="draft || transcribing">
      <textarea v-model.trim="draft" :disabled="transcribing || optimizing" maxlength="12000" @input="optimizationSummary = []"
                placeholder="语音转换结果将在这里显示，可直接审阅修改" />
      <div v-if="optimizationSummary.length" class="optimization-summary">
        <b>AI 优化概要</b>
        <ul><li v-for="(item, index) in optimizationSummary" :key="index">{{ item }}</li></ul>
      </div>
      <div class="actions">
        <button class="secondary" :disabled="!draft || optimizing || transcribing" @click="optimize">
          <van-loading v-if="optimizing" size="14px" />{{ optimizing ? '正在优化' : 'AI 优化' }}
        </button>
        <button class="secondary" :disabled="transcribing || optimizing" @click="reset">重录</button>
        <button class="primary" :disabled="!draft || transcribing || optimizing" @click="confirm">采用文本</button>
      </div>
    </template>
  </section>
</template>

<script>
import coreapi from '@/utils/coreapi';

export default {
  name: 'BossVoiceInput',
  props: { value: { type: String, default: '' } },
  data() {
    return {
      pressing: false, recording: false, transcribing: false, optimizing: false,
      recorder: null, stream: null, chunks: [], timer: null, duration: 0, shouldTranscribe: false,
      draft: '', audioUrl: '', lastTouchAt: 0, optimizationSummary: []
    };
  },
  computed: {
    statusText() {
      if (this.recording) return '松开后自动转成文字';
      if (this.transcribing) return '语音识别处理中';
      if (this.draft) return '请审阅文字后采用';
      return '按住录音按钮开始';
    }
  },
  watch: {
    value(next) { if (!next && !this.recording && !this.transcribing) this.reset(); }
  },
  beforeDestroy() { this.releaseResources(); },
  methods: {
    touchStart() { this.lastTouchAt = Date.now(); this.beginPress(); },
    touchEnd() { this.lastTouchAt = Date.now(); this.endPress(); },
    pressStart() { if (Date.now() - this.lastTouchAt > 700) this.beginPress(); },
    pressEnd() { if (Date.now() - this.lastTouchAt > 700) this.endPress(); },
    cancelPress() { this.pressing = false; this.stopRecording(false); },
    async beginPress() {
      if (this.recording || this.transcribing || this.optimizing) return;
      if (!navigator.mediaDevices || !window.MediaRecorder) return this.$toast('当前浏览器不支持录音，请使用最新版微信或浏览器');
      this.pressing = true;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!this.pressing) { stream.getTracks().forEach(track => track.stop()); return; }
        this.stream = stream; this.chunks = []; this.duration = 0;
        const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find(type => MediaRecorder.isTypeSupported(type));
        this.recorder = preferred ? new MediaRecorder(stream, { mimeType: preferred }) : new MediaRecorder(stream);
        this.recorder.ondataavailable = event => { if (event.data && event.data.size) this.chunks.push(event.data); };
        this.recorder.onstop = () => this.finishRecording();
        this.recorder.start(500); this.recording = true;
        this.timer = window.setInterval(() => { this.duration += 1; if (this.duration >= 300) this.endPress(); }, 1000);
      } catch (e) { this.pressing = false; this.$toast.fail('无法使用麦克风，请检查录音权限'); }
    },
    endPress() { this.pressing = false; this.stopRecording(true); },
    stopRecording(transcribe) {
      if (!this.recording || !this.recorder) return;
      this.shouldTranscribe = transcribe; this.recording = false;
      if (this.timer) window.clearInterval(this.timer); this.timer = null;
      if (this.recorder.state !== 'inactive') this.recorder.stop();
      if (this.stream) this.stream.getTracks().forEach(track => track.stop()); this.stream = null;
    },
    async finishRecording() {
      if (!this.shouldTranscribe) { this.chunks = []; return; }
      const mime = (this.recorder && this.recorder.mimeType) || 'audio/webm';
      const blob = new Blob(this.chunks, { type: mime }); this.chunks = [];
      if (blob.size < 800) return this.$toast('录音时间太短，请重新录制');
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl); this.audioUrl = URL.createObjectURL(blob);
      const extension = mime.indexOf('mp4') >= 0 ? 'm4a' : 'webm';
      const data = new FormData(); data.append('file', blob, `boss-voice-${Date.now()}.${extension}`);
      this.transcribing = true;
      try { const result = await coreapi.transcribeBossVoice(data); this.draft = result.text || ''; this.optimizationSummary = []; }
      catch (e) { this.$toast.fail(e.message || '语音转文字失败'); }
      finally { this.transcribing = false; }
    },
    async optimize() {
      if (!this.draft) return;
      this.optimizing = true;
      try {
        const result = await coreapi.optimizeBossVoiceText(this.draft);
        this.draft = result.title ? `${result.title}\n\n${result.text}` : result.text;
        this.optimizationSummary = Array.isArray(result.summary) ? result.summary.filter(Boolean) : [];
      } catch (e) { this.$toast.fail(e.message || '文本优化失败'); }
      finally { this.optimizing = false; }
    },
    confirm() { const text = this.draft.trim(); if (!text) return; this.$emit('input', text); this.$emit('confirmed', text); this.$toast.success('文字已采用'); },
    reset() { this.draft = ''; this.duration = 0; this.optimizationSummary = []; if (this.audioUrl) URL.revokeObjectURL(this.audioUrl); this.audioUrl = ''; },
    releaseResources() { if (this.timer) window.clearInterval(this.timer); if (this.stream) this.stream.getTracks().forEach(track => track.stop()); if (this.audioUrl) URL.revokeObjectURL(this.audioUrl); }
  }
};
</script>

<style lang="less" scoped>
.voice-input{margin:.28rem 0;padding:.26rem 0;border-top:1PX solid #ececf1;border-bottom:1PX solid #ececf1}.voice-input header{display:flex;align-items:center;justify-content:space-between}.voice-input header b,.voice-input header small{display:block}.voice-input header b{color:#34364a;font-size:.3rem}.voice-input header small{margin-top:.05rem;color:#9295a7;font-size:.22rem}.voice-input header>.van-icon{color:#d32f2f;font-size:.38rem}.record-row{margin-top:.2rem;display:flex;align-items:center;gap:.18rem}.record-button{min-width:2.2rem;height:.78rem;padding:0 .28rem;border:0;border-radius:.4rem;background:#d32f2f;color:#fff;display:flex;align-items:center;justify-content:center;gap:.12rem;font-size:.27rem}.record-button.recording{background:#a91e23;box-shadow:0 0 0 .1rem rgba(211,47,47,.12)}.record-button>.van-icon{font-size:.34rem}.record-row audio{min-width:0;height:.7rem;flex:1}.processing{margin-top:.2rem;color:#7f8392;font-size:.24rem;display:flex;align-items:center;gap:.1rem}.voice-input textarea{width:100%;height:2.35rem;margin-top:.2rem;padding:.2rem;border:1PX solid #e2e3ec;border-radius:.12rem;background:#fafaff;font-size:.27rem;line-height:1.55;resize:none;box-sizing:border-box}.actions{margin-top:.18rem;display:grid;grid-template-columns:1fr .8fr 1fr;gap:.12rem}.actions button{height:.72rem;border-radius:.12rem;font-size:.25rem}.actions .secondary{border:1PX solid #d32f2f;color:#d32f2f;background:#fff}.actions .primary{border:0;color:#fff;background:#d32f2f}.actions button:disabled{opacity:.45}.actions .van-loading{display:inline-block;margin-right:.06rem;vertical-align:middle}
.optimization-summary{margin-top:.16rem;padding:.18rem .2rem;color:#56596a;background:#fff7f7;border-left:.06rem solid #d32f2f;border-radius:.1rem}.optimization-summary b{display:block;color:#34364a;font-size:.25rem}.optimization-summary ul{margin:.08rem 0 0;padding-left:.34rem}.optimization-summary li{margin-top:.04rem;font-size:.23rem;line-height:1.5}
</style>



// WEBPACK FOOTER //
// src/components/boss/BossVoiceInput.vue